import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { Query } from "@tanstack/react-query";
import type { AsyncStorage } from "@tanstack/react-query-persist-client";
import { openDB } from "idb";

import {
  URL_PREVIEW_CACHE_MAX_AGE_MS,
  URL_PREVIEW_QUERY_KEY,
} from "@/utils/url-preview";

import {
  DISCOVER_WIDGET_CACHE_MAX_AGE_MS,
  DISCOVER_WIDGET_QUERY_KEY,
} from "./discover-widget";

export const FOCUS_MARKET_DATA_QUERY_KEY = "focus-market-data";
export const FOCUS_MARKET_DATA_CACHE_MAX_AGE_MS = 1000 * 60 * 15;

export const CREATOR_COIN_MARKET_FEES_QUERY_KEY = "creator-coin-market-fees";
export const CREATOR_COIN_MARKET_FEES_CACHE_MAX_AGE_MS =
  1000 * 60 * 60 * 24 * 7;

export const REFERRAL_CODE_QUERY_KEY = "referral-code";
export const REFERRAL_CODES_QUERY_KEY = "referral-codes";
export const REFERRAL_CODES_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30;

export const USER_ROLES_QUERY_KEY = "userRoles";
export const USER_ROLES_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24;

export const CURRENT_USER_SOCIAL_PROFILES_QUERY_KEY =
  "currentUserSocialProfiles";
export const USER_SOCIAL_PROFILE_QUERY_KEY = "userSocialProfile";
export const USER_SOCIAL_PROFILES_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 14;

export const FOCUS_PROFILE_QUERY_KEY = "focusProfile";
export const FOCUS_PROFILE_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24;

export const MY_FEED_POST_IDS_QUERY_KEY = "my-feed-post-ids";
export const MY_FEED_POST_IDS_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 6;

export type MyFeedPostIdsQueryParams = {
  publicKey: string;
  limit: number;
  offset: number;
  hideViewed?: boolean;
  hideNSFW?: boolean;
  useModeration?: boolean;
};

export const getMyFeedPostIdsQueryKey = ({
  publicKey,
  limit,
  offset,
  hideViewed = true,
  hideNSFW = false,
  useModeration = true,
}: MyFeedPostIdsQueryParams) =>
  [
    MY_FEED_POST_IDS_QUERY_KEY,
    publicKey,
    limit,
    offset,
    hideViewed,
    hideNSFW,
    useModeration,
  ] as const;

export const QUERY_CACHE_MAX_AGE_MS = REFERRAL_CODES_CACHE_MAX_AGE_MS;
export const QUERY_CACHE_BUSTER = "focus-query-cache-v1";
export const QUERY_CACHE_STORAGE_KEY = "focus-query-cache-v1";

const PERSISTED_QUERY_KEYS = new Set<unknown>([
  URL_PREVIEW_QUERY_KEY,
  FOCUS_MARKET_DATA_QUERY_KEY,
  CREATOR_COIN_MARKET_FEES_QUERY_KEY,
  REFERRAL_CODE_QUERY_KEY,
  REFERRAL_CODES_QUERY_KEY,
  USER_ROLES_QUERY_KEY,
  CURRENT_USER_SOCIAL_PROFILES_QUERY_KEY,
  USER_SOCIAL_PROFILE_QUERY_KEY,
  FOCUS_PROFILE_QUERY_KEY,
  MY_FEED_POST_IDS_QUERY_KEY,
  DISCOVER_WIDGET_QUERY_KEY,
]);

const PERSISTED_QUERY_MAX_AGE_MS = new Map<unknown, number>([
  [URL_PREVIEW_QUERY_KEY, URL_PREVIEW_CACHE_MAX_AGE_MS],
  [FOCUS_MARKET_DATA_QUERY_KEY, FOCUS_MARKET_DATA_CACHE_MAX_AGE_MS],
  [
    CREATOR_COIN_MARKET_FEES_QUERY_KEY,
    CREATOR_COIN_MARKET_FEES_CACHE_MAX_AGE_MS,
  ],
  [REFERRAL_CODE_QUERY_KEY, REFERRAL_CODES_CACHE_MAX_AGE_MS],
  [REFERRAL_CODES_QUERY_KEY, REFERRAL_CODES_CACHE_MAX_AGE_MS],
  [USER_ROLES_QUERY_KEY, USER_ROLES_CACHE_MAX_AGE_MS],
  [
    CURRENT_USER_SOCIAL_PROFILES_QUERY_KEY,
    USER_SOCIAL_PROFILES_CACHE_MAX_AGE_MS,
  ],
  [USER_SOCIAL_PROFILE_QUERY_KEY, USER_SOCIAL_PROFILES_CACHE_MAX_AGE_MS],
  [FOCUS_PROFILE_QUERY_KEY, FOCUS_PROFILE_CACHE_MAX_AGE_MS],
  [MY_FEED_POST_IDS_QUERY_KEY, MY_FEED_POST_IDS_CACHE_MAX_AGE_MS],
  [DISCOVER_WIDGET_QUERY_KEY, DISCOVER_WIDGET_CACHE_MAX_AGE_MS],
]);

export const shouldPersistQuery = (query: Query) => {
  if (
    query.state.status !== "success" ||
    !PERSISTED_QUERY_KEYS.has(query.queryKey[0])
  ) {
    return false;
  }

  const maxAge =
    PERSISTED_QUERY_MAX_AGE_MS.get(query.queryKey[0]) ?? QUERY_CACHE_MAX_AGE_MS;

  if (Date.now() - query.state.dataUpdatedAt > maxAge) {
    return false;
  }

  if (query.queryKey[0] === FOCUS_PROFILE_QUERY_KEY) {
    return Boolean(query.state.data);
  }

  return true;
};

const DATABASE_NAME = "focus-query-cache";
const STORE_NAME = "query-cache";

let databasePromise: ReturnType<typeof openDB> | null = null;

const getDatabase = async () => {
  if (typeof window === "undefined" || !("indexedDB" in window)) {
    return null;
  }

  try {
    databasePromise ??= openDB(DATABASE_NAME, 1, {
      upgrade(database) {
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME);
        }
      },
    });

    return await databasePromise;
  } catch (error) {
    databasePromise = null;

    if (process.env.NODE_ENV === "development") {
      console.warn("Failed to open persisted query cache", error);
    }

    return null;
  }
};

const queryCacheStorage: AsyncStorage<string> = {
  async getItem(key) {
    const database = await getDatabase();

    if (!database) {
      return null;
    }

    return (await database.get(STORE_NAME, key)) ?? null;
  },
  async setItem(key, value) {
    const database = await getDatabase();

    if (!database) {
      return;
    }

    await database.put(STORE_NAME, value, key);
  },
  async removeItem(key) {
    const database = await getDatabase();

    if (!database) {
      return;
    }

    await database.delete(STORE_NAME, key);
  },
};

export const queryPersister = createAsyncStoragePersister({
  key: QUERY_CACHE_STORAGE_KEY,
  storage: typeof window === "undefined" ? undefined : queryCacheStorage,
  throttleTime: 2_000,
});
